import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { connect } from 'react-redux';
import MenuItems from '../constants/MenuItems';
import LocalStorageService from '../services/LocalStorageService';

interface MobileFooterProps {
  userProfile?: any;
  companyProfile?: any;
}

const MobileFooter: React.FC<MobileFooterProps> = ({ userProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const localStorageService = LocalStorageService.getService();
  const roles = localStorageService.getUserRoles();

  // Don't show footer if not mobile or user not logged in
  if (!isMobile || !userProfile) {
    return null;
  }

  const hasUserMatchingRoles = (targetRoles: string[]) =>
    !targetRoles.some((role) => !!roles.find((x: string) => x === role));

  // Filter menu items based on user roles and get top-level items only
  const footerMenuItems = MenuItems.filter((item) => {
    // Must have matching role
    if (hasUserMatchingRoles(item.role) || item.showInFooter === false) {
      return false;
    }
    return true;
  }).slice(0, 4); // Limit to 4 items for better mobile footer layout

  const getCurrentValue = () => {
    // Find the active menu item based on current path
    const activeItem = footerMenuItems.find((item) => {
      if (item.subMenus && item.subMenus.length > 0) {
        // Check if any submenu matches current path
        const activeSubMenu = item.subMenus.find((subMenu) =>
          location.pathname.startsWith(subMenu.href));
        if (activeSubMenu) {
          return true;
        }
      } else if (location.pathname.startsWith(item.href)) {
        return true;
      }
      return false;
    });

    return activeItem ? activeItem.href : false;
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    const selectedItem = footerMenuItems.find((item) => item.href === newValue);
    if (selectedItem) {
      if (selectedItem.subMenus && selectedItem.subMenus.length > 0) {
        // Navigate to the first submenu item
        navigate(selectedItem.subMenus[0].href);
      } else {
        navigate(selectedItem.href);
      }
    }
  };

  const getIconComponent = (IconComponent: any) => <IconComponent fontSize="small" />;

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: { xs: 'block', lg: 'none' } // Only show on mobile
      }}
      elevation={3}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            color: theme.palette.text.secondary,
            minWidth: 'auto',
            padding: '4px 8px',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
        }}
      >
        {footerMenuItems.map((item) => (
          <BottomNavigationAction
            key={item.title}
            label={item.title}
            value={item.href}
            icon={getIconComponent(item.icon)}
            sx={{
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.625rem',
                lineHeight: 1.2,
                '&.Mui-selected': {
                  fontSize: '0.625rem',
                },
              },
            }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

const mapStateToProps = (state: any) => ({
  userProfile: state.profile?.user,
  companyProfile: state.profile?.company
});

export default connect(mapStateToProps)(MobileFooter);
